import React, { useRef, useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { WizardStepStatus, configurationWizardOptions } from "../utils/common";
import { toastNotification, createVsaTenant } from "../utils/apiFunctions";
import { useStateContext } from "../contexts/ContextProvider";
import { useEventListener } from "../utils/customHooks";
import { useQueryClient } from "@tanstack/react-query";

const LazyCompanyDetails = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/CompanyDetails"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});
const LazyInventorySettings = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/InventorySettings"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});
const LazyEndpointSettings = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/EndpointSettings"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});
const LazyFinalizeConfiguration = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/FinalizeConfiguration"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});

function renderDialogFooterWithThreeBtnsContent() {
  const footer = document.createElement("div");

  footer.innerHTML = `
      <gz-button id="keepAction" type="quiet">keep changes</gz-button>
      <div>
        <gz-button id="discardAction" color="negative" type='quiet' style="margin-left: 16px">discard</gz-button>
        <gz-button id="cancelAction" type="outline" color="secondary">cancel</gz-button>
      </div>
  `;
  return footer;
}

let initialStepIndex = 0;
let isConfigSet = false;
let tenantDataToSubmit = null;

let inventorySettingsData = null;
let companyDetailsSettingsData = null;
let endpointSettingsData = null;
let isFirstRender = true;

const ConfigurationWizard = ({ tenantData }) => {
  const { setIsNewTenant, setIsRegisteredInRmmi } = useStateContext();
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const [isConfigCompleted, setIsConfigCompleted] = useState(isConfigSet);
  const [isTenantRegistering, setIsTenantRegistering] = useState(false);
  const [tenantConfiguration, setTenantConfiguration] = useState(tenantData);
  const [companyDetailsSettings, setCompanyDetailsSettings] = useState(null);
  const [inventorySettings, setInventorySettings] = useState(null);
  const [agentSettings, setAgentSettings] = useState(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  let wizardRef = useRef(null);
  let nextBtnRef = useRef(null);
  let prevBtnRef = useRef(null);
  let resetBtnRef = useRef(null);

  useEffect(() => {
    if (isFirstRender) {
      companyDetailsSettingsData = (({ gzSettings, gzCompanyDetails }) => ({
        gzSettings,
        gzCompanyDetails,
      }))(tenantConfiguration);
      inventorySettingsData = (({ inventorySyncSettings, prefixSettings }) => ({
        inventorySyncSettings,
        prefixSettings,
      }))(tenantConfiguration);
      endpointSettingsData = (({
        endpointSyncSettings,
        packageDefaults,
        packageDownloadMethod,
      }) => ({ endpointSyncSettings, packageDefaults, packageDownloadMethod }))(
        tenantConfiguration
      );

      tenantDataToSubmit = {
        ...companyDetailsSettingsData,
        ...inventorySettingsData,
        ...endpointSettingsData,
      };

      setCompanyDetailsSettings({ ...companyDetailsSettingsData });
      setInventorySettings(inventorySettingsData);
      setAgentSettings(endpointSettingsData);

      isFirstRender = false;
    } else {
      if (currentStepIndex === 0) {
        companyDetailsSettingsData = (({ gzSettings, gzCompanyDetails }) => ({
          gzSettings,
          gzCompanyDetails,
        }))(tenantDataToSubmit);
        setCompanyDetailsSettings({ ...companyDetailsSettingsData });
      }
      if (currentStepIndex === 1) {
        inventorySettingsData = (({
          inventorySyncSettings,
          prefixSettings,
        }) => ({ inventorySyncSettings, prefixSettings }))(tenantDataToSubmit);
        setInventorySettings(inventorySettingsData);
      }
      if (currentStepIndex === 2) {
        endpointSettingsData = (({
          endpointSyncSettings,
          packageDefaults,
          packageDownloadMethod,
        }) => ({
          endpointSyncSettings,
          packageDefaults,
          packageDownloadMethod,
        }))(tenantDataToSubmit);
        setAgentSettings(endpointSettingsData);
      }
    }
  }, [tenantConfiguration, currentStepIndex]);

  useEffect(() => {
    if (isTenantRegistering) {
      wizardRef.current.getStepFromIndex(0).isEnabled = false;
      wizardRef.current.getStepFromIndex(1).isEnabled = false;
      wizardRef.current.getStepFromIndex(2).isEnabled = false;
    }
  }, [isTenantRegistering]);

  const createDialog = (wrapper) => {
    const dialogBody = document.createElement("div");

    dialogBody.innerHTML = `
        <p style="margin-bottom: 1rem;">Changes you have made will be lost.</p>
        `;

    const dialogFooter = renderDialogFooterWithThreeBtnsContent();

    const keepChangesBtn = dialogFooter.querySelector("#keepAction");
    const discardChangesBtn = dialogFooter.querySelector("#discardAction");
    const cancelActionBtn = dialogFooter.querySelector("#cancelAction");

    const dialogConfigs = {
      headline: "Are you sure you want to exit?",
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };

    const dialog =
      window.globalThis.nirvana.dependencies.services.buildDialogElement(
        dialogConfigs,
        wrapper
      );

    if (discardChangesBtn) {
      discardChangesBtn.addEventListener("click", () => {
        let hasUserInputs = false;
        for (let index = 0; index < 4; index++) {
          let step = wizardRef.current.getStepFromIndex(index);
          if (step.status === WizardStepStatus.COMPLETE) {
            hasUserInputs = true;
            break;
          }
        }

        if (hasUserInputs) {
          wizardRef.current.reset();
          initialStepIndex = wizardRef.current.activeIndex;
          wizardRef.current.getStepFromIndex(3).isEnabled = false;

          setTenantConfiguration(tenantData);

          setIsConfigCompleted(false);
          setCurrentStepIndex(initialStepIndex);
        }

        dialog.close();

        window.location.reload(false);
      });
    }

    if (cancelActionBtn) {
      cancelActionBtn.addEventListener("click", () => {
        dialog.dismiss();
      });
    }

    if (keepChangesBtn) {
      keepChangesBtn.addEventListener("click", () => {
        dialog.dismiss();
      });
    }
  };

  let incrementStep = () => {
    if (initialStepIndex < 4) {
      initialStepIndex++;
      setCurrentStepIndex((currentStepIndex) => currentStepIndex + 1);
    }
  };

  let decrementStep = () => {
    if (initialStepIndex >= 1) {
      initialStepIndex--;
      setCurrentStepIndex(initialStepIndex);
    }
  };

  const handleCompanyDetailsChange = (gzApiSettingsData) => {
    setCompanyDetailsSettings({
      ...companyDetailsSettings,
      gzSettings: {
        ...companyDetailsSettings.gzSettings,
        gzApiKey: gzApiSettingsData.gzApiKey,
        gzServerAddress: gzApiSettingsData.gzServerAddress,
      },
      gzCompanyDetails: {
        companyName: gzApiSettingsData.companyName,
        companyType: gzApiSettingsData.companyType,
        phone: gzApiSettingsData.phone,
        address: gzApiSettingsData.address,
        managed: gzApiSettingsData.managed,
      },
    });

    tenantDataToSubmit = {
      ...tenantConfiguration,
      gzSettings: {
        ...companyDetailsSettings.gzSettings,
        gzApiKey: gzApiSettingsData.gzApiKey,
        gzServerAddress: gzApiSettingsData.gzServerAddress,
      },
      gzCompanyDetails: {
        name: gzApiSettingsData.companyName,
        type: gzApiSettingsData.companyType,
        phone: gzApiSettingsData.phone,
        address: gzApiSettingsData.address,
        managed: gzApiSettingsData.managed,
      },
    };

    setTenantConfiguration(tenantDataToSubmit);
  };

  const handleInventorySettingsChange = (updatedInventorySettingsData) => {
    setTenantConfiguration({
      ...tenantConfiguration,
      inventorySyncSettings: updatedInventorySettingsData.inventorySyncSettings,
      prefixSettings: updatedInventorySettingsData.prefixSettings,
    });

    tenantDataToSubmit = {
      ...tenantConfiguration,
      inventorySyncSettings: updatedInventorySettingsData.inventorySyncSettings,
      prefixSettings: updatedInventorySettingsData.prefixSettings,
    };

    inventorySettingsData = (({ inventorySyncSettings, prefixSettings }) => ({
      inventorySyncSettings,
      prefixSettings,
    }))(tenantDataToSubmit);
  };

  const handleEndpointSettingsChange = (updatedEndpointSettingsData) => {
    setTenantConfiguration({
      ...tenantConfiguration,
      endpointSyncSettings: updatedEndpointSettingsData.endpointSyncSettings,
      packageDefaults: updatedEndpointSettingsData.packageDefaults,
      packageDownloadMethod: updatedEndpointSettingsData.packageDownloadMethod,
    });

    tenantDataToSubmit = {
      ...tenantConfiguration,
      endpointSyncSettings: updatedEndpointSettingsData.endpointSyncSettings,
      packageDefaults: updatedEndpointSettingsData.packageDefaults,
      packageDownloadMethod: updatedEndpointSettingsData.packageDownloadMethod,
    };

    endpointSettingsData = (({
      endpointSyncSettings,
      packageDefaults,
      packageDownloadMethod,
    }) => ({ endpointSyncSettings, packageDefaults, packageDownloadMethod }))(
      tenantDataToSubmit
    );
  };

  const handleStepChange = () => {
    initialStepIndex = wizardRef.current.activeIndex;
    setCurrentStepIndex(wizardRef.current.activeIndex);
  };

  const navNext = async () => {
    if (
      companyDetailsSettingsData.gzSettings.gzApiKey === null ||
      companyDetailsSettingsData.gzSettings.gzServerAddress === null
    ) {
      toastNotification(
        "No GravityZone API integration details provided.",
        "warning",
        false
      );
      return;
    }

    const currentIndex = wizardRef.current.activeIndex;
    const currentStep = wizardRef.current.getStepFromIndex(currentIndex);

    let stepOne = wizardRef.current.getStepFromIndex(0);
    let stepTwo = wizardRef.current.getStepFromIndex(1);
    let stepThree = wizardRef.current.getStepFromIndex(2);

    if (currentStep.step === 4) {
      if (
        stepOne.status !== WizardStepStatus.COMPLETE ||
        stepTwo.status !== WizardStepStatus.COMPLETE ||
        stepThree.status !== WizardStepStatus.COMPLETE
      ) {
        return;
      }
      setIsTenantRegistering(true);

      let registrationResult = await createVsaTenant(tenantDataToSubmit);

      if (registrationResult == null) {
        toastNotification("Configuration not saved. Try again later.", "error");
        setIsTenantRegistering(false);
      } else if (registrationResult.registrationId) {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.every((key) => 
              ['fetchTenantData', 'fetchUserInfo'].includes(key)
          )
      });
        navigate("/dashboard");
        toastNotification("Configuration successfully saved.", "success");

        setIsRegisteredInRmmi(true);
        setIsNewTenant(false);
        setIsTenantRegistering(false);
      }
      return;
    }
    wizardRef.current.completeStep(currentIndex);

    if (currentStep.status !== WizardStepStatus.COMPLETE) {
      return;
    }

    if (
      stepOne.status === WizardStepStatus.COMPLETE &&
      stepTwo.status === WizardStepStatus.COMPLETE &&
      stepThree.status === WizardStepStatus.COMPLETE
    ) {
      isConfigSet = true;
      setIsConfigCompleted(true);
    }

    wizardRef.current.next();
    incrementStep();
  };

  const navPrev = () => {
    wizardRef.current.previous();
    decrementStep();
  };

  const resetConfig = () => {
    const bodyElement = document.getElementsByTagName("body")[0];
    createDialog(bodyElement);
  };

  useEventListener({
    eventName: "click",
    handler: handleStepChange,
    ref: wizardRef,
  });
  useEventListener({ eventName: "click", handler: navNext, ref: nextBtnRef });
  useEventListener({ eventName: "click", handler: navPrev, ref: prevBtnRef });
  useEventListener({
    eventName: "click",
    handler: resetConfig,
    ref: resetBtnRef,
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none">
        <div className="ml-8 mb-2">
          <gz-title fontSize="20" fontWeight="500" style={{ opacity: "0.7" }}>
            Configuration
          </gz-title>
        </div>
        <div className="flex flex-col items-center justify-center h-28 border-b-1 bg-white top-auto pb-4">
          <div className="w-[1000px]">
            <gz-wizard
              id="configurationWizard"
              ref={wizardRef}
              options={JSON.stringify(configurationWizardOptions)}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full flex-1 overflow-auto initialConfigurationContent">
        {currentStepIndex === 0 && (
          <Suspense
            fallback={
              <div className="top-1/2 left-1/2 absolute">
                <gz-progress-spinner spinnerWidth="large" indeterminate />
              </div>
            }
          >
            <LazyCompanyDetails
              companySettings={companyDetailsSettings}
              updateTenantData={handleCompanyDetailsChange}
            />
          </Suspense>
        )}
        {currentStepIndex === 1 && (
          <Suspense
            fallback={
              <div className="top-1/2 left-1/2 absolute">
                <gz-progress-spinner spinnerWidth="large" indeterminate />
              </div>
            }
          >
            <LazyInventorySettings
              inventorySettings={inventorySettings}
              updateTenantData={handleInventorySettingsChange}
            />
          </Suspense>
        )}
        {currentStepIndex === 2 && (
          <Suspense
            fallback={
              <div className="top-1/2 left-1/2 absolute">
                <gz-progress-spinner spinnerWidth="large" indeterminate />
              </div>
            }
          >
            <LazyEndpointSettings
              agentSettings={agentSettings}
              updateTenantData={handleEndpointSettingsChange}
            />
          </Suspense>
        )}
        {currentStepIndex === 3 && (
          <Suspense
            fallback={
              <div className="top-1/2 left-1/2 absolute">
                <gz-progress-spinner spinnerWidth="large" indeterminate />
              </div>
            }
          >
            <div className="flex items-center align-middle justify-center">
              <LazyFinalizeConfiguration
                companyName={tenantConfiguration.gzCompanyDetails.name}
                isWizardCompleted={isConfigCompleted}
              />
            </div>
          </Suspense>
        )}
      </div>
      <div className="flex-none flex flex-row justify-between wizard-controls bottom-0 mb-3 bg-white max-h-[60px] shadow-[rgb(0,0,0,0.2)_0px_-6px_6px_-6px]">
        <div className="flex flex-row ml-12 mt-3 gap-3">
          {currentStepIndex === 3 ? (
            <gz-button
              id="nextBtn"
              type="full"
              color="primary"
              {...(isTenantRegistering
                ? { disabled: true, showSpinner: true }
                : {})}
              ref={nextBtnRef}
            >
              Save Configuration
            </gz-button>
          ) : (
            <gz-button
              id="nextBtn"
              type="outline"
              color="primary"
              ref={nextBtnRef}
            >
              Next
            </gz-button>
          )}
          {currentStepIndex === 0 ? (
            <gz-button
              id="prevBtn"
              type="quiet"
              color="secondary"
              ref={prevBtnRef}
              disabled
            >
              Back
            </gz-button>
          ) : (
            <gz-button
              id="prevBtn"
              type="quiet"
              color="secondary"
              {...(isTenantRegistering ? { disabled: true } : {})}
              ref={prevBtnRef}
            >
              Back
            </gz-button>
          )}
        </div>
        <div className="mr-12 mt-3">
          <gz-button
            id="resetConfigBtn"
            type="outline"
            color="secondary"
            {...(isTenantRegistering ? { disabled: true } : {})}
            ref={resetBtnRef}
          >
            Cancel
          </gz-button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationWizard;
